// Firebase B2B Service - Functions for accessing building data from Firebase

import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  collection,
  getDocs,
  getDoc,
  addDoc,
  deleteDoc,
  query,
  where,
  doc,
  setDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  Timestamp
} from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import {
  getAuth,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendEmailVerification,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail
} from 'firebase/auth';

// Your Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyC-sHHOuYyZ4GZyVZRyugaAZleiKpE8ZCw",
  authDomain: "dwella-510eb.firebaseapp.com",
  projectId: "dwella-510eb",
  storageBucket: "dwella-510eb.firebasestorage.app",
  messagingSenderId: "93404206905",
  appId: "1:93404206905:web:243ea0054397ad2808fae8",
  measurementId: "G-3MVHFTVBTC"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const db = getFirestore(app);
const auth = getAuth(app);
const storage = getStorage(app);

// ==================== GET ALL BUILDINGS ====================

export const getAllBuildings = async () => {
  try {
    // Get all reviews from top-level 'reviews' collection
    const reviewsRef = collection(db, 'reviews');
    const reviewsSnapshot = await getDocs(reviewsRef);
    const allReviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Get all improvements from top-level 'improvements' collection
    const improvementsRef = collection(db, 'improvements');
    const improvementsSnapshot = await getDocs(improvementsRef);
    const allImprovements = improvementsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Group reviews by buildingAddress
    const buildingsMap = new Map();

    allReviews.forEach(review => {
      const address = review.buildingAddress;
      if (!address) return; // Skip reviews without address

      if (!buildingsMap.has(address)) {
        buildingsMap.set(address, {
          id: address.replace(/\s+/g, '-').toLowerCase(), // Create ID from address
          address: address,
          reviews: [],
          improvements: [],
          location: review.location || null // Use first review's location if available
        });
      }

      buildingsMap.get(address).reviews.push(review);
    });

    // Add improvements to their respective buildings
    allImprovements.forEach(improvement => {
      const address = improvement.buildingAddress;
      if (address && buildingsMap.has(address)) {
        buildingsMap.get(address).improvements.push(improvement);
      }
    });

    // Convert map to array
    const buildings = Array.from(buildingsMap.values());

    return buildings;
  } catch (error) {
    console.error('Error getting buildings:', error);
    throw new Error('Failed to load buildings: ' + error.message);
  }
};

// ==================== GET BUILDING BY ADDRESS ====================

export const getBuildingByAddress = async (address) => {
  try {
    // Normalize the address for comparison (lowercase, trim whitespace)
    const normalizedSearchAddress = address.toLowerCase().trim();

    // Get all reviews from top-level collection
    const reviewsRef = collection(db, 'reviews');
    const reviewsSnapshot = await getDocs(reviewsRef);
    const allReviews = reviewsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Find reviews matching this address
    const matchingReviews = allReviews.filter(review => {
      const normalizedBuildingAddress = (review.buildingAddress || '').toLowerCase().trim();
      return normalizedBuildingAddress.includes(normalizedSearchAddress) ||
             normalizedSearchAddress.includes(normalizedBuildingAddress);
    });

    if (matchingReviews.length === 0) {
      throw new Error('Building not found at the specified address. Please verify the address and try again.');
    }

    // Check if building has sufficient reviews (minimum 3 for critical mass)
    if (matchingReviews.length < 3) {
      throw new Error(`Insufficient data: This building only has ${matchingReviews.length} review(s). A minimum of 3 reviews is required to generate a reliable risk report.`);
    }

    // Get the actual building address from the first review
    const buildingAddress = matchingReviews[0].buildingAddress;

    // Get all improvements from top-level collection
    const improvementsRef = collection(db, 'improvements');
    const improvementsSnapshot = await getDocs(improvementsRef);
    const allImprovements = improvementsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Filter improvements for this building
    const matchingImprovements = allImprovements.filter(improvement =>
      improvement.buildingAddress === buildingAddress
    );

    return {
      id: buildingAddress.replace(/\s+/g, '-').toLowerCase(),
      address: buildingAddress,
      reviews: matchingReviews,
      improvements: matchingImprovements,
      location: matchingReviews[0].location || null
    };
  } catch (error) {
    console.error('Error getting building by address:', error);
    throw error;
  }
};

// ==================== GET PENDING VERIFICATIONS ====================

export const getPendingVerifications = async () => {
  try {
    // Query all improvements from top-level collection with status 'pending'
    const improvementsRef = collection(db, 'improvements');
    const q = query(improvementsRef, where('status', '==', 'pending'));
    const improvementsSnapshot = await getDocs(q);

    const pendingVerifications = improvementsSnapshot.docs.map(doc => ({
      id: doc.id,
      buildingId: doc.data().buildingAddress?.replace(/\s+/g, '-').toLowerCase(),
      buildingAddress: doc.data().buildingAddress,
      ...doc.data()
    }));

    return pendingVerifications;
  } catch (error) {
    console.error('Error getting pending verifications:', error);
    throw new Error('Failed to load verifications: ' + error.message);
  }
};

// ==================== APPROVE IMPROVEMENT ====================

export const approveImprovement = async (improvementId) => {
  try {
    // Update improvement in top-level collection
    const improvementRef = doc(db, 'improvements', improvementId);

    await updateDoc(improvementRef, {
      status: 'approved',
      verifiedAt: Timestamp.now(),
      verifiedBy: 'admin'
    });

    return { success: true, message: 'Improvement approved' };
  } catch (error) {
    console.error('Error approving improvement:', error);
    throw new Error('Failed to approve improvement: ' + error.message);
  }
};

// ==================== REJECT IMPROVEMENT ====================

export const rejectImprovement = async (improvementId, reason) => {
  try {
    // Update improvement in top-level collection
    const improvementRef = doc(db, 'improvements', improvementId);

    await updateDoc(improvementRef, {
      status: 'rejected',
      rejectedAt: Timestamp.now(),
      rejectedBy: 'admin',
      rejectionReason: reason
    });

    return { success: true, message: 'Improvement rejected' };
  } catch (error) {
    console.error('Error rejecting improvement:', error);
    throw new Error('Failed to reject improvement: ' + error.message);
  }
};

// ==================== AUTHENTICATION ====================

export const loginWithEmail = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
  } catch (error) {
    console.error('Error signing in:', error);
    throw new Error(error.message);
  }
};

/**
 * Sign up a Property Manager or Lender via the website.
 * Creates Firebase Auth account + Firestore user doc with the correct accountType.
 * The Toice app reads accountType on login and routes them to the right dashboard.
 */
export const signUpB2B = async (email, password, name, accountType) => {
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Send verification email
    await sendEmailVerification(user);

    // Create user profile in Firestore — same 'users' collection the app reads
    await setDoc(doc(db, 'users', user.uid), {
      email,
      userId: user.uid,
      name,
      accountType, // 'property_manager' or 'lender'
      points: 0,
      level: 1,
      badges: [],
      reviewCount: 0,
      createdAt: Timestamp.now(),
      emailVerified: false,
      signedUpVia: 'website',
    });

    return user;
  } catch (error) {
    console.error('Error signing up:', error);
    throw new Error(error.message);
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
  } catch (error) {
    console.error('Error signing out:', error);
    throw new Error(error.message);
  }
};

export const getCurrentUser = () => {
  return auth.currentUser;
};

export const resetPassword = async (email) => {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    console.error('Error sending password reset:', error);
    throw new Error(error.message);
  }
};

export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// ==================== GET BUILDINGS BY MANAGER ====================

export const getBuildingsByManager = async (userId) => {
  try {
    // Step 1: Find this PM's record by userId and get their claimed buildings
    const pmRef = collection(db, 'propertyManagers');
    const pmQuery = query(pmRef, where('userId', '==', userId));
    const pmSnapshot = await getDocs(pmQuery);

    let claimedAddresses = [];
    if (!pmSnapshot.empty) {
      const pmData = pmSnapshot.docs[0].data();
      if (pmData.claimedBuildings && Array.isArray(pmData.claimedBuildings)) {
        claimedAddresses = pmData.claimedBuildings
          .map(b => (typeof b === 'string' ? b : b.address))
          .filter(Boolean);
      }
    }

    // Step 2: Get reviews for each claimed building
    const reviewsRef = collection(db, 'reviews');
    const reviewsSnapshot = await getDocs(reviewsRef);
    const allReviews = reviewsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Step 3: Get all improvements submitted for these buildings
    const improvementsRef = collection(db, 'improvements');
    const improvementsSnapshot = await getDocs(improvementsRef);
    const allImprovements = improvementsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Step 4: Build the buildings map — include claimed addresses even if no reviews yet
    const buildingsMap = new Map();
    claimedAddresses.forEach(address => {
      buildingsMap.set(address, {
        id: address.replace(/\s+/g, '-').toLowerCase(),
        address,
        reviews: [],
        improvements: [],
      });
    });

    allReviews.forEach(review => {
      const address = review.buildingAddress;
      if (!address || !buildingsMap.has(address)) return;
      buildingsMap.get(address).reviews.push(review);
    });

    allImprovements.forEach(improvement => {
      const address = improvement.buildingAddress;
      if (address && buildingsMap.has(address)) {
        buildingsMap.get(address).improvements.push(improvement);
      }
    });

    return Array.from(buildingsMap.values());
  } catch (error) {
    console.error('Error getting buildings by manager:', error);
    throw new Error('Failed to load your buildings: ' + error.message);
  }
};

// ==================== CREATE IMPROVEMENT / REMEDIATION EVIDENCE ====================

export const createImprovement = async (buildingAddress, userId, description, reviewId, file) => {
  try {
    let evidenceURL = null;
    let evidenceFileName = null;

    // Upload evidence file if provided
    if (file) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const storageRef = ref(storage, `improvements/${buildingAddress.replace(/\s+/g, '_')}/${Date.now()}_${safeName}`);
      await uploadBytes(storageRef, file);
      evidenceURL = await getDownloadURL(storageRef);
      evidenceFileName = file.name;
    }

    // Save to Firestore
    const improvementsRef = collection(db, 'improvements');
    const docRef = await addDoc(improvementsRef, {
      buildingAddress,
      description,
      submittedBy: userId,
      linkedReviewId: reviewId || null,
      status: 'pending',
      evidenceURL,
      evidenceFileName,
      createdAt: Timestamp.now(),
    });

    return { id: docRef.id, evidenceURL };
  } catch (error) {
    console.error('Error creating improvement:', error);
    throw new Error('Failed to submit remediation: ' + error.message);
  }
};

// ==================== CALCULATE DISTANCE BETWEEN TWO COORDINATES ====================

// Haversine formula to calculate distance in miles
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// ==================== GET AREA COMPARISON ====================

export const getAreaComparison = async (targetBuilding, allBuildings = null) => {
  try {
    // If allBuildings not provided, fetch them
    if (!allBuildings) {
      allBuildings = await getAllBuildings();
    }

    // Check if target building has location data
    if (!targetBuilding.location || !targetBuilding.location.latitude || !targetBuilding.location.longitude) {
      return null; // Can't compare without location
    }

    const targetLat = targetBuilding.location.latitude;
    const targetLon = targetBuilding.location.longitude;

    // Calculate distances and risk scores for all other buildings
    const buildingsWithDistance = allBuildings
      .filter(building => {
        // Exclude the target building and buildings without location
        return building.address !== targetBuilding.address &&
               building.location &&
               building.location.latitude &&
               building.location.longitude &&
               building.reviews &&
               building.reviews.length >= 3; // Only include buildings with sufficient data
      })
      .map(building => {
        const distance = calculateDistance(
          targetLat,
          targetLon,
          building.location.latitude,
          building.location.longitude
        );

        // Calculate risk score for this building (we'll need to import the risk engine)
        // For now, we'll calculate a simple score based on reviews
        const totalIssues = building.reviews.length;
        const criticalIssues = building.reviews.filter(r => r.severity === 'high').length;
        const unresolvedIssues = building.reviews.filter(r => r.status !== 'addressed').length;

        // Simple risk score calculation (0-100)
        const riskScore = Math.min(100, Math.round(
          (totalIssues * 5) +
          (criticalIssues * 10) +
          (unresolvedIssues * 3)
        ));

        return {
          ...building,
          distance,
          riskScore
        };
      })
      .sort((a, b) => a.distance - b.distance);

    // Get buildings within different radii
    const quarterMile = buildingsWithDistance.filter(b => b.distance <= 0.25);
    const oneMile = buildingsWithDistance.filter(b => b.distance <= 1);
    const twoMiles = buildingsWithDistance.filter(b => b.distance <= 2);
    const threeMiles = buildingsWithDistance.filter(b => b.distance <= 3);

    // Get 10 closest buildings
    const tenClosest = buildingsWithDistance.slice(0, 10);

    // Calculate averages
    const calcAvg = (buildings) => {
      if (buildings.length === 0) return 'N/A';
      const avg = buildings.reduce((sum, b) => sum + b.riskScore, 0) / buildings.length;
      return Math.round(avg);
    };

    return {
      quarterMileAvg: calcAvg(quarterMile),
      quarterMileCount: quarterMile.length,
      oneMileAvg: calcAvg(oneMile),
      oneMileCount: oneMile.length,
      twoMileAvg: calcAvg(twoMiles),
      twoMileCount: twoMiles.length,
      threeMileAvg: calcAvg(threeMiles),
      threeMileCount: threeMiles.length,
      tenClosestAvg: calcAvg(tenClosest),
      tenClosestCount: tenClosest.length,
      nearbyBuildings: tenClosest.map(b => ({
        address: b.address,
        distance: Math.round(b.distance * 100) / 100, // Round to 2 decimals
        riskScore: b.riskScore
      }))
    };
  } catch (error) {
    console.error('Error calculating area comparison:', error);
    return null;
  }
};

// ==================== GET BUILDING WITH COMPARISON ====================

export const getBuildingWithComparison = async (address) => {
  try {
    // Get the target building
    const building = await getBuildingByAddress(address);

    // Get all buildings for comparison
    const allBuildings = await getAllBuildings();

    // Calculate area comparison
    const areaComparison = await getAreaComparison(building, allBuildings);

    // Add comparison data to building
    return {
      ...building,
      areaComparison
    };
  } catch (error) {
    console.error('Error getting building with comparison:', error);
    throw error;
  }
};

// ==================== GET USER ACCOUNT TYPE ====================

export const getUserAccountType = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data().accountType || 'property_manager';
    }
    return 'property_manager'; // default fallback
  } catch (error) {
    console.error('Error getting user account type:', error);
    return 'property_manager';
  }
};

// ==================== GET BUILDINGS BY LENDER ====================

export const getBuildingsByLender = async (userId) => {
  try {
    // Get user's watched buildings list
    const userDocSnap = await getDoc(doc(db, 'users', userId));
    let watchedAddresses = [];
    if (userDocSnap.exists() && userDocSnap.data().watchedBuildings) {
      watchedAddresses = userDocSnap.data().watchedBuildings;
    }

    if (watchedAddresses.length === 0) {
      return [];
    }

    // Get all reviews
    const reviewsRef = collection(db, 'reviews');
    const reviewsSnapshot = await getDocs(reviewsRef);
    const allReviews = reviewsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Get all improvements
    const improvementsRef = collection(db, 'improvements');
    const improvementsSnapshot = await getDocs(improvementsRef);
    const allImprovements = improvementsSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

    // Group reviews by watched building addresses
    const buildingsMap = new Map();

    allReviews.forEach(review => {
      const address = review.buildingAddress;
      if (!address || !watchedAddresses.includes(address)) return;

      if (!buildingsMap.has(address)) {
        buildingsMap.set(address, {
          id: address.replace(/\s+/g, '-').toLowerCase(),
          address: address,
          reviews: [],
          improvements: [],
          location: review.location || null
        });
      }

      buildingsMap.get(address).reviews.push(review);
    });

    // Add improvements
    allImprovements.forEach(improvement => {
      const address = improvement.buildingAddress;
      if (address && buildingsMap.has(address)) {
        buildingsMap.get(address).improvements.push(improvement);
      }
    });

    return Array.from(buildingsMap.values());
  } catch (error) {
    console.error('Error getting buildings by lender:', error);
    throw new Error('Failed to load portfolio buildings: ' + error.message);
  }
};

// ==================== ADD/REMOVE BUILDING FROM PORTFOLIO ====================

export const addBuildingToPortfolio = async (userId, buildingAddress) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      watchedBuildings: arrayUnion(buildingAddress)
    });
    return { success: true };
  } catch (error) {
    console.error('Error adding building to portfolio:', error);
    throw new Error('Failed to add building: ' + error.message);
  }
};

export const removeBuildingFromPortfolio = async (userId, buildingAddress) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      watchedBuildings: arrayRemove(buildingAddress)
    });
    return { success: true };
  } catch (error) {
    console.error('Error removing building from portfolio:', error);
    throw new Error('Failed to remove building: ' + error.message);
  }
};

// ==================== PROPERTY DOCUMENT UPLOADS ====================

export const uploadPropertyDocument = async (buildingAddress, file, documentType, userId) => {
  try {
    // Upload file to Storage
    const storageRef = ref(storage, `property_documents/${buildingAddress}/${file.name}`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);

    // Save metadata to Firestore
    const docRef = await addDoc(collection(db, 'property_documents'), {
      propertyAddress: buildingAddress,
      documentType: documentType,
      url: downloadURL,
      fileName: file.name,
      fileSize: file.size,
      storagePath: `property_documents/${buildingAddress}/${file.name}`,
      uploadedBy: userId,
      uploadedAt: Timestamp.now()
    });

    return { id: docRef.id, url: downloadURL };
  } catch (error) {
    console.error('Error uploading document:', error);
    throw new Error('Failed to upload document: ' + error.message);
  }
};

export const getPropertyDocuments = async (buildingAddress) => {
  try {
    const q = query(
      collection(db, 'property_documents'),
      where('propertyAddress', '==', buildingAddress)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting property documents:', error);
    throw new Error('Failed to load documents: ' + error.message);
  }
};

export const deletePropertyDocument = async (docId, storagePath) => {
  try {
    await deleteDoc(doc(db, 'property_documents', docId));
    if (storagePath) {
      const storageRef = ref(storage, storagePath);
      await deleteObject(storageRef);
    }
  } catch (error) {
    console.error('Error deleting document:', error);
    throw new Error('Failed to delete document: ' + error.message);
  }
};

// ==================== INSPECTION NOTES ====================

export const saveInspectionNote = async (buildingAddress, userId, inspectionData) => {
  try {
    const noteRef = await addDoc(collection(db, 'inspection_notes'), {
      propertyAddress: buildingAddress,
      userId: userId,
      rating: inspectionData.rating,
      unitsInspected: inspectionData.unitsInspected,
      comments: inspectionData.comments,
      createdAt: Timestamp.now()
    });
    return { id: noteRef.id };
  } catch (error) {
    console.error('Error saving inspection note:', error);
    throw new Error('Failed to save inspection note: ' + error.message);
  }
};

export const getInspectionNotes = async (buildingAddress) => {
  try {
    const q = query(
      collection(db, 'inspection_notes'),
      where('propertyAddress', '==', buildingAddress)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting inspection notes:', error);
    throw new Error('Failed to load inspection notes: ' + error.message);
  }
};

// Save a demo scheduling request
export const saveDemoRequest = async (requestData) => {
  try {
    const docRef = await addDoc(collection(db, 'demoRequests'), {
      ...requestData,
      createdAt: Timestamp.now(),
      status: 'pending'
    });
    return docRef.id;
  } catch (error) {
    console.error('Error saving demo request:', error);
    throw new Error('Failed to submit demo request: ' + error.message);
  }
};

// ==================== PCA REPORTS ====================

/**
 * Upload a third-party report PDF (PCA, Phase I, Appraisal, Zoning, ALTA)
 * and save its metadata to the pca_reports collection.
 */
export const savePCAReport = async (propertyAddress, file, meta, userId, userRole) => {
  try {
    // Upload PDF to Storage
    const storagePath = `pca_reports/${propertyAddress.replace(/\s+/g, '_')}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const pdfUrl = await getDownloadURL(storageRef);

    const docRef = await addDoc(collection(db, 'pca_reports'), {
      propertyAddress,
      reportType:  meta.reportType  || 'PCA',
      reportFirm:  meta.reportFirm  || '',
      reportDate:  meta.reportDate  || '',
      pdfUrl,
      fileName:    file.name,
      storagePath,
      uploadedBy:  userId,
      uploadedByRole: userRole || 'property_manager',
      uploadedAt:  Timestamp.now(),
      sharedWith:  [],
      loanInfo:    meta.loanInfo || {},
    });

    return { id: docRef.id, pdfUrl };
  } catch (error) {
    console.error('Error saving PCA report:', error);
    throw new Error('Failed to upload report: ' + error.message);
  }
};

export const getPCAReports = async (propertyAddress) => {
  try {
    const q = query(
      collection(db, 'pca_reports'),
      where('propertyAddress', '==', propertyAddress)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting PCA reports:', error);
    throw new Error('Failed to load reports: ' + error.message);
  }
};

// ==================== PCA ITEMS ====================

/**
 * Save a single PCA line item (immediate repair or capital reserve).
 */
export const savePCAItem = async (propertyAddress, itemData, userId) => {
  try {
    const docRef = await addDoc(collection(db, 'pca_items'), {
      propertyAddress,
      pcaReportId:   itemData.pcaReportId   || null,
      priority:      itemData.priority       || 'immediate',
      category:      itemData.category       || 'General',
      description:   itemData.description    || '',
      estimatedCost: itemData.estimatedCost  || 0,
      dueDate:       itemData.dueDate        || null,
      yearDue:       itemData.yearDue        || null,
      status:        itemData.status         || 'open',
      completedAt:   null,
      completedBy:   null,
      evidenceUrls:  [],
      evidenceNotes: '',
      createdAt:     Timestamp.now(),
      createdBy:     userId,
    });
    return { id: docRef.id };
  } catch (error) {
    console.error('Error saving PCA item:', error);
    throw new Error('Failed to save item: ' + error.message);
  }
};

export const getPCAItems = async (propertyAddress) => {
  try {
    const q = query(
      collection(db, 'pca_items'),
      where('propertyAddress', '==', propertyAddress)
    );
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error('Error getting PCA items:', error);
    throw new Error('Failed to load items: ' + error.message);
  }
};

export const updatePCAItem = async (itemId, updates) => {
  try {
    const itemRef = doc(db, 'pca_items', itemId);
    const payload = { ...updates };

    // Auto-set completedAt when marking complete
    if (updates.status === 'complete' && !updates.completedAt) {
      payload.completedAt = Timestamp.now();
    }
    // Auto-clear completedAt when reopening
    if (updates.status === 'open') {
      payload.completedAt = null;
    }

    await updateDoc(itemRef, payload);
    return { success: true };
  } catch (error) {
    console.error('Error updating PCA item:', error);
    throw new Error('Failed to update item: ' + error.message);
  }
};

/**
 * Upload a file as evidence for a PCA item completion.
 * Returns the download URL.
 */
export const uploadPCAEvidence = async (itemId, file, userId) => {
  try {
    const storagePath = `pca_evidence/${itemId}/${Date.now()}_${file.name}`;
    const storageRef = ref(storage, storagePath);
    await uploadBytes(storageRef, file);
    const url = await getDownloadURL(storageRef);

    // Append URL to the item's evidenceUrls array
    const itemRef = doc(db, 'pca_items', itemId);
    await updateDoc(itemRef, {
      evidenceUrls: arrayUnion(url),
    });

    return url;
  } catch (error) {
    console.error('Error uploading evidence:', error);
    throw new Error('Failed to upload evidence: ' + error.message);
  }
};

// ==================== BUILDING CLAIMS ====================

/**
 * PM submits a claim for a building by uploading ownership proof (PSA or title).
 * Creates a pending claim in Firestore; admin must approve before the building
 * appears in the PM's dashboard.
 */
export const submitBuildingClaim = async (userId, userEmail, address, docType, file) => {
  let docURL = null;
  let docFileName = null;

  if (file) {
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const storageRef = ref(
      storage,
      `buildingClaims/${userId}/${address.replace(/\s+/g, '_')}/${Date.now()}_${safeName}`
    );
    await uploadBytes(storageRef, file);
    docURL = await getDownloadURL(storageRef);
    docFileName = file.name;
  }

  const claimsRef = collection(db, 'buildingClaims');
  const docRef = await addDoc(claimsRef, {
    userId,
    userEmail,
    address,
    docType,       // 'psa' | 'title'
    docURL,
    docFileName,
    status: 'pending',
    submittedAt: Timestamp.now(),
    reviewedAt: null,
    rejectionReason: null,
  });

  return { id: docRef.id };
};

/**
 * Returns all building claims submitted by a specific PM (any status).
 */
export const getMyBuildingClaims = async (userId) => {
  const claimsRef = collection(db, 'buildingClaims');
  const q = query(claimsRef, where('userId', '==', userId));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Returns all pending building claims (admin use).
 */
export const getPendingBuildingClaims = async () => {
  const claimsRef = collection(db, 'buildingClaims');
  const q = query(claimsRef, where('status', '==', 'pending'));
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
};

/**
 * Admin approves a building claim.
 * Sets the claim to 'approved' and adds the address to the PM's claimedBuildings.
 */
export const approveBuildingClaim = async (claimId) => {
  // 1. Get the claim
  const claimRef = doc(db, 'buildingClaims', claimId);
  const claimSnap = await getDoc(claimRef);
  if (!claimSnap.exists()) throw new Error('Claim not found');
  const { userId, address } = claimSnap.data();

  // 2. Mark claim approved
  await updateDoc(claimRef, { status: 'approved', reviewedAt: Timestamp.now() });

  // 3. Find or create the propertyManagers doc for this user
  const pmRef = collection(db, 'propertyManagers');
  const pmQuery = query(pmRef, where('userId', '==', userId));
  const pmSnap = await getDocs(pmQuery);

  if (!pmSnap.empty) {
    // Add address to existing doc
    await updateDoc(pmSnap.docs[0].ref, {
      claimedBuildings: arrayUnion(address),
    });
  } else {
    // Create new propertyManagers doc
    await addDoc(pmRef, {
      userId,
      claimedBuildings: [address],
      createdAt: Timestamp.now(),
    });
  }
};

/**
 * Admin rejects a building claim with an optional reason.
 */
export const rejectBuildingClaim = async (claimId, reason = '') => {
  const claimRef = doc(db, 'buildingClaims', claimId);
  await updateDoc(claimRef, {
    status: 'rejected',
    rejectionReason: reason,
    reviewedAt: Timestamp.now(),
  });
};

// Export all functions
export default {
  getAllBuildings,
  getBuildingByAddress,
  getBuildingWithComparison,
  getBuildingsByManager,
  getBuildingsByLender,
  addBuildingToPortfolio,
  removeBuildingFromPortfolio,
  getPendingVerifications,
  approveImprovement,
  rejectImprovement,
  getAreaComparison,
  uploadPropertyDocument,
  getPropertyDocuments,
  deletePropertyDocument,
  saveInspectionNote,
  getInspectionNotes,
  getUserAccountType,
  loginWithEmail,
  logout,
  getCurrentUser,
  onAuthChange,
  saveDemoRequest,
  signUpB2B,
  // PCA tracking
  savePCAReport,
  getPCAReports,
  savePCAItem,
  getPCAItems,
  updatePCAItem,
  uploadPCAEvidence,
};